import { BloodPressureReading } from "@/model/BloodPressureReading";
import { Cell, CellProps } from "@/screens/patient-details/Cell";
import NewDataTooltipIndicator from "@/screens/patient-details/NewDataTooltipIndicator";
import cn from "@/util/cn";
import { DateTimeFormatter, LocalDate, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import { ComponentPropsWithoutRef } from "react";
import {
  backgroundColorForClassification,
  foregroundColorForClassification,
} from "./colorForClassification";

const dayMonthFormatter = DateTimeFormatter.ofPattern("MMM d").withLocale(Locale.US);
const timeFormatter = DateTimeFormatter.ofPattern("HH:mm").withLocale(Locale.US);

type BloodPressuresCellProps = {
  readings: BloodPressureReading[];
  date: LocalDate;
  status: "default" | "new";
} & CellProps;

const BloodPressuresCell = ({
  readings,
  date,
  status,
  className,
  ...otherProps
}: BloodPressuresCellProps) => {
  const isNew = status === "new";
  const hasData = readings.length > 0;
  const isInFuture = date.isAfter(LocalDate.now(ZoneId.SYSTEM));

  return (
    <Cell className={cn("flex flex-col gap-2", className)} outline {...otherProps}>
      <div className="flex flex-row-reverse items-center">
        <p className={cn("text-nowrap", isInFuture ? "text-primary/38" : "text-primary/90")}>
          {dayMonthFormatter.format(date)}
        </p>
        {isNew && <NewDataTooltipIndicator className="me-2" />}
      </div>
      {!hasData ? (
        <div className="h-full w-full pb-1 text-center text-sm italic text-primary/38">-</div>
      ) : (
        <div className="flex flex-col gap-2">
          {readings.map((reading) => (
            <BloodPressureReadingLine key={reading.id} reading={reading} />
          ))}
        </div>
      )}
    </Cell>
  );
};

type BloodPressureReadingLineProps = {
  reading: BloodPressureReading;
} & ComponentPropsWithoutRef<"div">;

const BloodPressureReadingLine = ({
  reading,
  className,
  ...otherProps
}: BloodPressureReadingLineProps) => (
  <div
    className={cn(
      "flex items-center rounded-xs px-2 py-[2px]",
      backgroundColorForClassification(reading.classification),
      className
    )}
    {...otherProps}
  >
    <p className={cn("me-2 shrink-0 grow text-nowrap text-sm text-primary/90")}>
      {timeFormatter.format(reading.dateTime)}
    </p>
    <p
      className={cn(
        "shrink-0 text-nowrap font-bold",
        foregroundColorForClassification(reading.classification)
      )}
    >
      {reading.bloodPressure}
    </p>
  </div>
);

export default BloodPressuresCell;
